// Locked question set for group quizzes.
//
// Every group, regardless of which quiz type a member would otherwise have
// taken, uses the 12 short-quiz questions as its comparison surface. The
// short-quiz set is a strict subset of the long quiz's Phase 1, so:
//
//   - Cube position (3D) is comparable across all members regardless of quiz
//     type, since it derives from the same axes.
//   - Per-question agreement comparison is comparable because every member
//     answers exactly these 12 questions in the group flow.
//
// The IDs are stored on the group doc at creation time (group.questionIds).
// Members load the full Question objects via loadGroupQuizQuestions().

import { loadAllQuestions } from '../question-loader';
import type { Locale } from '../locale-context';

// Returns the originalIds (e.g. "P05", "TBE-CR2") of the 12 short-quiz
// questions in stable TSV order. Caller should shuffle for display.
export async function getLockedQuestionIds(locale: Locale = 'en'): Promise<string[]> {
  const all = await loadAllQuestions(locale);
  const ids: string[] = [];
  for (const [originalId, q] of all) {
    if (q.shortQuiz && q.phase === 1) {
      ids.push(originalId);
    }
  }
  return ids;
}

// The richer comparison surface for all-long-quiz groups: the 36 Phase-1
// core questions (P01..P36). The 12 short-quiz ids are a strict subset, so a
// short taker can't cover these — long groups require the long quiz.
//
// Tiebreaker caveat: the long quiz's social 12 vary per taker. Some core
// social questions carry a removal_priority and get swapped out for boundary
// tiebreakers (TBE-/TBA-) depending on where the taker lands (see
// generateLongQuizQuestions). So a member may not have answered every one of
// these 36 — econ (12) + gov (12) are stable, the social subset is where gaps
// appear. That's intentional and handled downstream: per-question comparison
// is computed across whoever answered each question, and the 3D cube position
// — which folds in the tiebreakers AND the 24 macro-cell Phase-2 questions —
// is the always-present headline comparison. The boundary tiebreakers
// themselves are excluded from the surface (they're not common across takers).
//
// Computed client-side at /group/new (the TSV loads reliably in the
// browser); the server just stores the resulting ids on the group.
export async function getLongQuizComparisonIds(locale: Locale = 'en'): Promise<string[]> {
  const all = await loadAllQuestions(locale);
  const ids: string[] = [];
  for (const [originalId, q] of all) {
    if (q.phase === 1 && q.qType === 'core') {
      ids.push(originalId);
    }
  }
  return ids;
}
